import { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { isGarbageEventName } from '../lib/eventName';

export interface SearchResult {
  id: string;
  type: 'city' | 'team' | 'venue' | 'garage' | 'event';
  name: string;
  subtitle: string;
  slug: string;
  href: string;
}

export function useUniversalSearch(query: string) {
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const trimmed = query.trim();
    if (!trimmed) {
      setResults([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    debounceRef.current = setTimeout(async () => {
      const pattern = `%${trimmed}%`;

      const [cities, teams, venues, garages, events] = await Promise.all([
        supabase
          .from('cities')
          .select('id, name, slug, state')
          .ilike('name', pattern)
          .limit(5),
        supabase
          .from('teams')
          .select('id, name, slug, sport, league')
          .ilike('name', pattern)
          .limit(5),
        supabase
          .from('venues')
          .select('id, name, slug, venue_type')
          .ilike('name', pattern)
          .limit(5),
        supabase
          .from('garages')
          .select('id, name, slug, address')
          .ilike('name', pattern)
          .limit(5),
        supabase
          .from('events')
          .select('id, name, slug, event_type, starts_at')
          .ilike('name', pattern)
          .limit(5),
      ]);

      const mapped: SearchResult[] = [];

      (cities.data ?? []).forEach((c) =>
        mapped.push({
          id: c.id,
          type: 'city',
          name: c.name,
          subtitle: c.state,
          slug: c.slug,
          href: `/cities/${c.slug}`,
        }),
      );

      (teams.data ?? []).forEach((t) =>
        mapped.push({
          id: t.id,
          type: 'team',
          name: t.name,
          subtitle: `${t.league} ${t.sport}`,
          slug: t.slug,
          href: `/teams/${t.slug}`,
        }),
      );

      (venues.data ?? []).forEach((v) =>
        mapped.push({
          id: v.id,
          type: 'venue',
          name: v.name,
          subtitle: v.venue_type,
          slug: v.slug,
          href: `/venues/${v.slug}`,
        }),
      );

      (garages.data ?? []).forEach((g) =>
        mapped.push({
          id: g.id,
          type: 'garage',
          name: g.name,
          subtitle: g.address,
          slug: g.slug,
          href: `/garages/${g.slug}`,
        }),
      );

      (events.data ?? [])
        .filter((e) => !isGarbageEventName(e.name))
        .forEach((e) =>
        mapped.push({
          id: e.id,
          type: 'event',
          name: e.name,
          subtitle: e.event_type,
          slug: e.slug,
          href: `/events/${e.slug}`,
        }),
      );

      setResults(mapped);
      setLoading(false);
    }, 200);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query]);

  return { results, loading };
}
